import { keepPreviousData } from '@tanstack/react-query'

/**
 * FF-QA-006 / FF-QA-020: the sidebar's company logo as ONE cached query.
 *
 * `AuthenticatedAttachmentImage` fetched the logo on every mount with no
 * retry: 46 of 47 audited boots re-downloaded it, and the one aborted fetch
 * (a navigation mid-request) left the sidebar with no mark at all. Here the
 * bytes are fetched once per logo URL per page session (`staleTime` and
 * `gcTime` infinite — a replaced logo is a NEW attachment at a new URL, so a
 * stale hit is impossible), an abort or a transport fault is retried, and a
 * change of URL keeps the old image on screen until the new one lands.
 *
 * The API answers with `ETag` + `Cache-Control: private, max-age` for
 * branding, so even a cold page load revalidates instead of re-downloading.
 *
 * An object URL is meaningless after a reload, so this root is excluded from
 * the persisted query cache (`NEVER_PERSIST` in providers.tsx).
 */
export const COMPANY_LOGO_QUERY_ROOT = 'company-logo'

/** Retry budget: an abort or a 5xx is worth two more tries; a 4xx is not. */
export const COMPANY_LOGO_RETRIES = 2

export function shouldRetryCompanyLogo(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status
  const clientError = typeof status === 'number' && status >= 400 && status < 500
  return !clientError && failureCount < COMPANY_LOGO_RETRIES
}

export function companyLogoQueryOptions(
  path: string,
  fetcher: (path: string) => Promise<Response>,
  toObjectUrl: (blob: Blob) => string = (blob) => URL.createObjectURL(blob),
) {
  return {
    queryKey: [COMPANY_LOGO_QUERY_ROOT, path] as const,
    queryFn: async (): Promise<string> => {
      const response = await fetcher(path)
      if (!response.ok) throw Object.assign(new Error(`Logo request failed (${response.status})`), { status: response.status })
      return toObjectUrl(await response.blob())
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: shouldRetryCompanyLogo,
    retryDelay: (attempt: number) => Math.min(1_000 * 2 ** attempt, 4_000),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  }
}
